import { Text, View, Page, Image, Link } from '@react-pdf/renderer';

import {
  Portfolio,
  PublicEventDetailsResponse,
} from '@hubs101/booking-api-skd-client/lib/types';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { globalStyles } from '../../styles';
import { styles } from './styles';
import { buildTargetGroupsItems } from '@utils/targetGroupsUtils';
import { iconsMap } from '@composites/PDF/constants';
import bgImage from '@assets/images/bg_speakers_white.png';
import { truncateText } from '../../../utils';
import { APP_HOST } from '@utils/apiHooks';

export interface TargetGroupItem {
  title: string;
  icon: string;
  content: Array<{ percentage: number; title: string }>;
}

interface WhoAndWhyPageProps {
  event: PublicEventDetailsResponse;
  portfolio?: Portfolio;
  header: JSX.Element;
  footer: JSX.Element;
}

const WhoAndWhyPage = ({
  event,
  portfolio,
  header,
  footer,
}: WhoAndWhyPageProps) => {
  const { t } = useTranslation();

  const eventStatisticsItems = useMemo(() => {
    const raw = (event as any)?.website_content?.statistics;

    const arr: any[] = Array.isArray(raw)
      ? raw
      : typeof raw === 'string'
        ? (() => {
            try {
              const p = JSON.parse(raw);
              return Array.isArray(p)
                ? p
                : p && typeof p === 'object'
                  ? Object.values(p)
                  : [];
            } catch {
              return [];
            }
          })()
        : raw && typeof raw === 'object'
          ? Object.values(raw)
          : [];

    return arr
      .filter(Boolean)
      .map((s: any) => ({
        description: String(s?.description ?? ''),
        title: String(s?.value ?? ''),
        icon: String(s?.icon ?? '').replace(/-/g, '_') || 'circle_info',
        order: Number(s?.order ?? 0),
      }))
      .sort((a, b) => a.order - b.order);
  }, [event?.website_content?.statistics])?.slice(0, 12);

  const targetGroupsItems = useMemo<TargetGroupItem[]>(
    () => buildTargetGroupsItems((event as any)?.website_content?.targetgroups),
    [event?.website_content?.targetgroups]
  );

  const MAX_NR_OF_TARGET_GROUP_CONTENT = 7;
  const hasMoreTargetGroupItemsThan4 = targetGroupsItems?.length > 4;

  return (
    <Page size="A4" orientation="portrait" style={styles.page}>
      {header}

      {/* Snapshot – EventName The Essentials at a Glance​ */}
      <View
        style={{
          ...styles.contentWrapper,
          marginTop: hasMoreTargetGroupItemsThan4 ? 10 : 20,
        }}
      >
        {/* TITLE ​ */}

        <Text
          style={{
            ...styles.titleText,
            color: event?.design?.themeColor,
            width: '70%',
          }}
        >
          {t('the-show-at-the-glance')}
        </Text>
        <View
          style={{
            ...globalStyles.lineBar,
            borderColor: event?.design?.themeColor,
            marginLeft: 0,
            marginBottom: 10,
            width: 89,
          }}
        />
        <View style={styles.statisticsGrid}>
          {eventStatisticsItems?.length > 0 &&
            eventStatisticsItems?.slice(0, 9)?.map((item, index) => {
              return (
                <View key={index} style={styles.statisticsItem}>
                  <Text
                    style={{
                      ...styles.icon,
                      marginTop: 5,
                      color: event?.design?.themeColor,
                      fontSize: hasMoreTargetGroupItemsThan4 ? 10 : 14,
                    }}
                  >
                    {iconsMap[item?.icon] || iconsMap.default}
                  </Text>
                  <View style={styles.textWrapper}>
                    <Text
                      style={{
                        ...styles.statTitle,
                        fontSize: hasMoreTargetGroupItemsThan4 ? 11 : 14,
                      }}
                    >
                      {item.title}
                    </Text>
                    <Text
                      style={{
                        ...styles.statDescription,
                        fontSize: hasMoreTargetGroupItemsThan4 ? 7 : 9,
                      }}
                    >
                      {truncateText(item.description, 95)}
                    </Text>
                  </View>
                </View>
              );
            })}

          {eventStatisticsItems?.length > 9 && (
            <View
              style={{
                ...styles.furtherInformationWrapper,
              }}
            >
              <Text
                style={{
                  ...styles.icon,
                  color: event?.design?.themeColor,
                  fontSize: 12,
                }}
              >
                {iconsMap.external_link_line}
              </Text>
              <Link
                href={`${APP_HOST}/events/${event?.slug}${t('who-and-why-url')}`}
                style={{
                  ...styles.furtherInformationLink,
                  color: event?.design?.themeColor ?? 'black',
                }}
              >
                {t('further-information')}
              </Link>
            </View>
          )}
        </View>
      </View>

      {/* The Decision-Makers That Matter – Your Target Audience in Focus​ */}
      <View>
        {/* TITLE ​ */}
        <View style={{ paddingHorizontal: 40, width: '100%' }}>
          <Text
            style={{
              ...styles.titleText,
              color: event?.design?.themeColor,
              width: '70%',
            }}
          >
            {t('the-decision-makers-that-matters')}
          </Text>
          <View
            style={{
              ...globalStyles.lineBar,
              borderColor: event?.design?.themeColor,
              marginLeft: 0,
              marginBottom: hasMoreTargetGroupItemsThan4 ? 10 : 20,
              width: 89,
            }}
          />
        </View>

        {/* STATISTICS ​ */}
        <View
          style={{
            ...styles.targetGroupsBackgroundWrapper,
            marginTop: hasMoreTargetGroupItemsThan4 ? 0 : 10,
            height: hasMoreTargetGroupItemsThan4 ? 440 : 335,
          }}
        >
          <Image
            src={bgImage}
            style={{
              ...styles.targetGroupsBackgroundImage,
              height: hasMoreTargetGroupItemsThan4 ? 440 : 335,
            }}
          />

          <View
            style={{
              ...styles.contentWrapper,
              paddingBottom: hasMoreTargetGroupItemsThan4 ? 0 : 20,
              paddingTop: 10,
              marginTop: hasMoreTargetGroupItemsThan4 ? 0 : 20,
              height: hasMoreTargetGroupItemsThan4 ? 440 : 335,
            }}
          >
            <View style={styles.targetGroupsWrapper}>
              {targetGroupsItems?.slice(0, 8)?.map(item => (
                <View key={item.title}>
                  <View style={styles.targetGroupsItem}>
                    <View style={styles.targetGroupsHeader}>
                      <Text style={{ ...styles.icon, marginTop: 5 }}>
                        {iconsMap[item?.icon] || iconsMap.default}
                      </Text>
                      <Text style={styles.columnTitle}>
                        {truncateText(item.title, 18)}
                      </Text>
                    </View>

                    <View>
                      {item?.content
                        ?.slice(0, MAX_NR_OF_TARGET_GROUP_CONTENT)
                        ?.map((content, index) => (
                          <View key={index} style={{ marginBottom: 6 }}>
                            <View style={styles.rowSpaceBetween}>
                              <Text
                                style={{
                                  ...styles.smallText,
                                  width: '80%',
                                  textAlign: 'left',
                                }}
                              >
                                {truncateText(
                                  content.title,
                                  hasMoreTargetGroupItemsThan4 ? 22 : 44
                                )}
                              </Text>
                              <Text
                                style={{
                                  ...styles.smallText,
                                  width: '20%',
                                  textAlign: 'right',
                                }}
                              >
                                {content.percentage}%
                              </Text>
                            </View>
                            <View style={styles.progressBarContainer}>
                              <View
                                style={{
                                  ...styles.progressBarFill,
                                  width: `${content.percentage}%`,
                                  backgroundColor:
                                    event?.design?.themeColor ?? 'black',
                                }}
                              />
                            </View>
                          </View>
                        ))}
                    </View>
                  </View>
                </View>
              ))}
            </View>
          </View>
        </View>
      </View>

      {footer}
    </Page>
  );
};

export default WhoAndWhyPage;
